import { Role } from 'auth';
import Big from 'big.js';
import classNames from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import omit from 'lodash/omit';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useLocation, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import ActivityStatusBadge from '@/modules/common/components/ActivityStatusBadge.tsx';
import RegisterClassesSelectPaginate from '@/modules/common/components/RegisterClassesSelectPaginate.tsx';
import { DateVariant, dateToPrint } from '@/modules/common/utils/date-format.ts';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import { IssuerTransferOperationInfoModal } from '@/modules/issuer-dashboard/components/modals/IssuerTransfersOperationInfoModal.tsx';
import { transferTabFilters } from '@/modules/issuer-dashboard/components/operation-tabs/IssuerTransfersTab.tsx';
import { SelectHolder } from '@/modules/issuer-dashboard/components/transactions/SelectHolder.tsx';
import { TransactionsHeaderAction } from '@/modules/issuer-dashboard/pages/IssuerTransactions.tsx';
import {
  useIssuerTransferListQuery,
  useTransferArchiveByIdMutation,
} from '@/modules/issuer-dashboard/queries/transfers.tsx';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import {
  BodyCell,
  ConfirmModal,
  FilterItem,
  ModalProviderContext,
  PageLayout,
  Spinner,
  Table,
  TableBodyColumn,
  TableHeadColumn,
  TooltipNew,
  getSelectOptionByEnum,
} from 'ui';
import { useIsMobile, useSaveState } from 'ui/hooks';
import { TrashGrayIcon } from 'ui/icons';
import {
  goodFormatNumber,
  goodFormatUSDCurrency,
  goodFormatUSDCurrencyLowPrecision,
} from 'ui/utils';
import { TransactionStatus, TransferPaginateResponse } from 'api';

type TypeHead =
  | 'ID'
  | 'Status'
  | 'Class'
  | 'Sender'
  | 'Receiver'
  | 'Date'
  | 'Unit/Shares'
  | 'Price'
  | 'Total'
  | '';

const IssuerTransfers = () => {
  const [isDeleted, setIsDeleted] = useState<boolean | undefined>();
  return (
    <PageLayout
      title="Transfers"
      headerAction={
        <TransactionsHeaderAction
          buttonName="Add Transfer"
          openTypeTransaction="Transfer"
          isDeleted={isDeleted}
          setIsDeleted={setIsDeleted}
        />
      }
      contentClassName="px-0 py-2"
      classNameTitle="flex-row justify-between items-center"
    >
      <TransfersBody isDeleted={isDeleted} />
    </PageLayout>
  );
};

interface TransfersBodyProps {
  rightBlock?: ReactNode;
  hideChooseRegisterClass?: boolean;
  register_class_id?: number;
  isDeleted?: boolean;
}

export const TransfersBody = ({
  rightBlock,
  hideChooseRegisterClass = false,
  register_class_id,
  isDeleted,
}: TransfersBodyProps) => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isMobile } = useIsMobile();
  const { openModal } = useContext(ModalProviderContext);
  const { registerId } = useOutletContext<IssuerDashboardWrapperOutletContext>();
  const [filters, setFilters] = useSaveState<transferTabFilters>(
    {
      register_class_id,
      is_deleted: isDeleted,
      status: TransactionStatus.Completed,
    },
    `filters-transfers-${registerId}`,
  );
  const [archivingId, setArchivingId] = useState<number | null>(null);

  const {
    data: issuerActivitiesList,
    fetchNextPage,
    hasNextPage,
    isLoading: isLoadingActivitiesList,
    isFetching,
    isError,
  } = useIssuerTransferListQuery(
    {
      page: 1,
      page_size: 30,
      ...filters,
      register_class_id: register_class_id ?? filters.register_class_id,
      is_deleted: isDeleted ?? filters.is_deleted,
    },
    false,
    Number(registerId),
  );

  const { mutateAsync: mutateAsyncTransferArchive, isLoading: isLoadingTransferArchive } =
    useTransferArchiveByIdMutation();

  const calculateTotal = useCallback((data: TransferPaginateResponse): string => {
    const { price_per_share, quantity } = data;

    const calculatedValue = new Big(quantity || 0).mul(price_per_share || 0).valueOf();
    return goodFormatUSDCurrencyLowPrecision(+calculatedValue);
  }, []);

  const headItems = useMemo<TableHeadColumn<TypeHead>[]>(() => {
    let items: TableHeadColumn<TypeHead>[] = [
      { title: 'ID', className: '' },
      { title: 'Status', className: '' },
      {
        title: 'Class',
        className: 'min-w-[140px]',
      },
      {
        title: 'Sender',
        className: 'min-w-[140px]',
      },
      {
        title: 'Receiver',
        className: 'min-w-[140px]',
      },
      {
        title: 'Date',
        className: 'min-w-[140px]',
      },
      {
        title: 'Unit/Shares',
        className: '',
      },
      {
        title: 'Price',
        className: '',
      },
      {
        title: 'Total',
        className: '',
      },
      {
        title: '',
      },
    ];

    if (hideChooseRegisterClass) {
      items = items.filter(item => item.title !== 'Class');
    }

    if (isDeleted) {
      items = items.slice(0, -1);
    }

    return items;
  }, [hideChooseRegisterClass, isDeleted]);

  const handleOpenOperationInfoModal = useCallback(
    (item: TransferPaginateResponse) => {
      openModal(IssuerTransferOperationInfoModal, {
        id: item.id,
        isDeleted,
      });
    },
    [isDeleted, openModal],
  );

  const handleGoToHolderPage = useCallback(
    (entityId?: number) => {
      if (!entityId) return;

      navigate(
        {
          pathname: issuerDashboardPaths.dynamic.holder(entityId.toString()),
          search: params.toString(),
        },
        {
          state: { from: pathname + '?' + params.toString() },
        },
      );
    },
    [navigate, params, pathname],
  );

  const handleGoToClassPage = useCallback(
    (classId?: number) => {
      if (!classId) return;

      navigate(
        {
          pathname: issuerDashboardPaths.dynamic.classById(classId.toString()),
          search: params.toString(),
        },
        {
          state: { from: pathname + '?' + params.toString() },
        },
      );
    },
    [navigate, params, pathname],
  );

  const handleArchive = useCallback(
    (id: number) => {
      openModal(ConfirmModal, {
        title: 'Archive transfer?',
        onConfirm: async () => {
          try {
            setArchivingId(id);
            await mutateAsyncTransferArchive(id);
          } finally {
            setArchivingId(null);
          }
        },
      });
    },
    [mutateAsyncTransferArchive, openModal],
  );

  const bodyItems = useMemo<TableBodyColumn[]>(() => {
    if (!issuerActivitiesList) {
      return [];
    }

    let result: TableBodyColumn[] = [];

    issuerActivitiesList.pages.forEach(page => {
      result = result.concat(
        page.data?.map(data => {
          const total = calculateTotal(data);
          let body: BodyCell[] = [
            {
              cell: (
                <span className="text-sm leading-[22px] text-[#0369A1] font-[500]">#{data.id}</span>
              ),
            },
            {
              cell: (
                <span>
                  <ActivityStatusBadge status={data.status || ''} />
                </span>
              ),
            },
            {
              cell: (
                <span
                  onClick={e => {
                    e.stopPropagation();
                    handleGoToClassPage(data.register_class?.id);
                  }}
                  className={classNames(
                    'text-sm whitespace-nowrap max-w-[190px] overflow-hidden text-ellipsis',
                    {
                      'text-blue-700 font-medium cursor-pointer hover:underline':
                        !!data.register_class?.id,
                    },
                  )}
                >
                  {data.register_class?.class_name ?? '-'}
                </span>
              ),
            },
            {
              cell: (
                <div
                  onClick={e => {
                    e.stopPropagation();
                    handleGoToHolderPage(data.from_shareholder_entity?.id);
                  }}
                  className={classNames(
                    'text-sm whitespace-nowrap max-w-[190px] overflow-hidden text-ellipsis',
                    {
                      'text-blue-700 font-medium cursor-pointer hover:underline':
                        !!data.from_shareholder_entity?.id,
                    },
                  )}
                >{`${data.from_shareholder_entity?.entity_name}`}</div>
              ),
            },
            {
              cell: (
                <div
                  onClick={e => {
                    e.stopPropagation();
                    handleGoToHolderPage(data.to_shareholder_entity?.id);
                  }}
                  className={classNames(
                    'text-sm whitespace-nowrap max-w-[190px] overflow-hidden text-ellipsis',
                    {
                      'text-blue-700 font-medium cursor-pointer hover:underline':
                        !!data.to_shareholder_entity?.id,
                    },
                  )}
                >{`${data.to_shareholder_entity?.entity_name}`}</div>
              ),
            },
            {
              cell: (
                <div className="">
                  {dateToPrint(
                    data.confirmation_date || data.requested_at_date,
                    DateVariant.Table,
                  ) || '-'}
                </div>
              ),
            },
            {
              cell: <span>{goodFormatNumber(data.quantity ?? 0)}</span>,
            },
            {
              cell: <span>{goodFormatUSDCurrency(data.price_per_share ?? 0)}</span>,
            },
            {
              cell: <span>{total}</span>,
            },
            {
              cell: (
                <span>
                  {isLoadingTransferArchive && archivingId === data.id ? (
                    <Spinner size="sm" className="m-0" />
                  ) : (
                    <TooltipNew text="Archive" className="w-5 h-5 min-w-5 min-h-5">
                      <img
                        src={TrashGrayIcon}
                        alt="Archive"
                        className="w-5 h-5 min-w-5 min-h-5 cursor-pointer hover:opacity-75"
                        onClick={e => {
                          e.stopPropagation();
                          handleArchive(data.id);
                        }}
                      />
                    </TooltipNew>
                  )}
                </span>
              ),
            },
          ];

          if (hideChooseRegisterClass) {
            body = body.filter((_, i) => i !== 2); //remove class
          }

          if (isDeleted) {
            body = body.slice(0, -1);
          }

          return {
            cells: body,
            onClick: () => handleOpenOperationInfoModal(data),
          };
        }),
      );
    });

    return result;
  }, [
    archivingId,
    calculateTotal,
    handleArchive,
    handleGoToClassPage,
    handleGoToHolderPage,
    handleOpenOperationInfoModal,
    hideChooseRegisterClass,
    isDeleted,
    isLoadingTransferArchive,
    issuerActivitiesList,
  ]);

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex items-start justify-between gap-4 w-full px-3 sm:px-6">
        <FiltersPanel
          setFilters={setFilters}
          filters={filters}
          registerId={registerId}
          isFetching={isFetching}
        />
        {rightBlock}
      </div>
      <Table
        className="my-0"
        classNameHead="border-t-0"
        withRounded={isMobile}
        withBorder={true}
        withBorderX={false}
        headItems={headItems}
        items={bodyItems}
        isLoading={isLoadingActivitiesList}
        isError={isError}
        hasNextPageInfinite={hasNextPage}
        fetchNextPageInfinite={fetchNextPage}
        blockIdInfinite="scrollable"
      />
    </div>
  );
};

interface FiltersPanelProps {
  filters: transferTabFilters;
  setFilters: Dispatch<SetStateAction<transferTabFilters>>;
  registerId: string;
  isFetching: boolean;
}

const FiltersPanel = ({ filters, setFilters, registerId, isFetching }: FiltersPanelProps) => {
  const handleChangeStatus = useCallback(
    (status: TransactionStatus | null) => {
      setFilters(filters => (status ? { ...filters, status } : omit(filters, 'status')));
    },
    [setFilters],
  );

  const dateValue =
    filters.from_requested_at_date && filters.to_requested_at_date
      ? ([dayjs(filters.from_requested_at_date), dayjs(filters.to_requested_at_date)] as [
          Dayjs,
          Dayjs,
        ])
      : undefined;

  const handleChangeDate = useCallback(
    (date: [Dayjs, Dayjs] | null) => {
      setFilters(filters => {
        return date
          ? {
              ...filters,
              from_requested_at_date: date[0].toISOString(),
              to_requested_at_date: date[1].endOf('d').toISOString(),
            }
          : omit(filters, ['from_requested_at_date', 'to_requested_at_date']);
      });
    },
    [setFilters],
  );

  const handleChangeHolder = useCallback(
    (general_shareholder_id?: number) => {
      setFilters(filters => {
        return general_shareholder_id
          ? {
              ...filters,
              general_shareholder_id,
            }
          : omit(filters, ['general_shareholder_id']);
      });
    },
    [setFilters],
  );

  const handleChangeRegisterClass = useCallback(
    (registerClassId: number | null) => {
      setFilters(filters => {
        return registerClassId
          ? {
              ...filters,
              register_class_id: +registerClassId,
            }
          : omit(filters, ['register_class_id']);
      });
    },
    [setFilters],
  );

  return (
    <div className="flex gap-2 items-center flex-wrap w-full">
      <FilterItem
        placeholder="Status"
        type="select"
        value={filters.status}
        options={getSelectOptionByEnum<TransactionStatus>(TransactionStatus).map(item => ({
          ...item,
          label: item.label?.replace('Unsuccessfull', 'Unsuccessful'),
        }))}
        onChange={handleChangeStatus}
        className="rounded-lg"
        classes={{
          label: 'w-[85px] shrink-0',
          element: 'w-full min-w-[200px] [&>div>div]:gap-1 [&>div>div]:md:!gap-3',
        }}
      />

      <RegisterClassesSelectPaginate
        registerId={+registerId}
        onChange={item => {
          handleChangeRegisterClass(item?.value.id || null);
        }}
        value={filters?.register_class_id}
        isMulti={false}
        placeholder="Class"
        label=""
        className="flex items-center border px-2 rounded-lg gap-2 w-full font-[Inter] text-sm leading-[22px] max-w-[350px] min-w-[200px]"
        classNameSelect="w-full"
        formFieldTitle=""
        showBorderBottom={false}
        withBorder={false}
        clearable={true}
      />

      <SelectHolder
        setShareholder={handleChangeHolder}
        shareholderId={filters.general_shareholder_id}
        registerId={registerId}
        placeholder="Holder"
        label=""
        isClearable={true}
        className="max-w-[250px] min-w-[200px]"
      />

      <FilterItem
        placeholder="Date"
        type="date"
        onChange={handleChangeDate}
        value={dateValue}
        className="rounded-lg"
      />

      {isFetching && <Spinner size="md" variant="primary" />}
    </div>
  );
};

export default withPrivateAccess(IssuerTransfers, [Role.SR_ISSUER, Role.SR_ISSUER_ADMIN]);
